import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  AlertCircle,
  FileStack,
  FileText,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Search,
  Loader2,
  RotateCcw,
  Trash2,
  XCircle,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import {
  useFailedDocuments,
  useDocumentPreview,
  useDocumentProcessLogs,
  useRetryDocument,
  useDeleteDocument,
  useBulkRetryDocuments,
  type ScannedDocument,
} from "@/hooks/useDocuments";
import { supabase } from "@/integrations/supabase/client";
import { PDFViewer } from "@/components/documents/PDFViewer";
import { formatDistanceToNow } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

interface FailedDocumentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PAGE_SIZE = 10;

function DetailPanel({ document }: { document: ScannedDocument }) {
  const { data: previewUrl, isLoading: previewLoading } = useDocumentPreview(document.id);
  const { data: processLogs } = useDocumentProcessLogs(document.id);
  const retryDocument = useRetryDocument();
  const deleteDocument = useDeleteDocument();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showProcessLog, setShowProcessLog] = useState(false);

  const handleRetry = () => {
    retryDocument.mutate(document.id);
  };

  const handleDelete = () => {
    deleteDocument.mutate(document.id, {
      onSuccess: () => setShowDeleteDialog(false),
    });
  };

  const formatPacificTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  return (
    <>
      <div className="flex flex-col h-full overflow-y-auto">
        {/* PDF Preview */}
        <div className="flex-1 min-h-0 p-4">
          {previewLoading ? (
            <div className="flex items-center justify-center h-full border rounded-lg bg-muted">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : previewUrl ? (
            <PDFViewer url={previewUrl} className="h-full" />
          ) : (
            <div className="flex flex-col items-center justify-center h-full border rounded-lg bg-muted/50 text-muted-foreground">
              <FileText className="h-10 w-10 mb-2" />
              <p className="text-sm">Preview unavailable</p>
            </div>
          )}
        </div>

        {/* Error & Actions */}
        <div className="px-4 py-3 border-t shrink-0 space-y-3">
          {/* Error message */}
          {document.error_message && (
            <div className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start gap-2">
                <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                <p className="text-sm text-red-600 dark:text-red-400">
                  {document.error_message}
                </p>
              </div>
            </div>
          )}

          {/* Retry count */}
          {document.retry_count > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <RotateCcw className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">Retry attempts:</span>
              <Badge variant="outline">{document.retry_count}</Badge>
            </div>
          )}

          {/* Processing Log - Collapsible */}
          {processLogs && processLogs.length > 0 && (
            <Collapsible open={showProcessLog} onOpenChange={setShowProcessLog}>
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-between h-7 px-2 text-xs hover:bg-muted"
                >
                  <span className="flex items-center gap-1.5">
                    <FileText className="h-3 w-3" />
                    Processing Log ({processLogs.length} events)
                  </span>
                  {showProcessLog ? (
                    <ChevronUp className="h-3 w-3" />
                  ) : (
                    <ChevronDown className="h-3 w-3" />
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                  {processLogs.map((log) => (
                    <div
                      key={log.id}
                      className={`p-2 rounded text-xs border ${
                        log.event === 'gemini_failed'
                          ? "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800"
                          : "bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-700"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium capitalize">
                          {log.event.replace(/_/g, ' ')}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {formatPacificTime(log.occurred_at)}
                        </span>
                      </div>
                      {log.details?.error && (
                        <div className="text-[10px] text-red-500 dark:text-red-400 font-mono break-all">
                          {log.details.error}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          <Separator />

          {/* Action buttons */}
          <div className="flex gap-2">
            <Button
              className="flex-1"
              onClick={handleRetry}
              disabled={retryDocument.isPending}
            >
              {retryDocument.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Retrying...
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Retry OCR
                </>
              )}
            </Button>
            <Button
              variant="destructive"
              onClick={() => setShowDeleteDialog(true)}
              disabled={deleteDocument.isPending}
            >
              {deleteDocument.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{document.original_filename}" from the processing queue?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function FailedDocumentsDialog({ open, onOpenChange }: FailedDocumentsDialogProps) {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const { data: documents, isLoading } = useFailedDocuments();
  const bulkRetry = useBulkRetryDocuments();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Bulk delete state
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setPage(0);
      setSearch("");
      setSelectedDocId(null);
    }
    onOpenChange(open);
  };

  const query = search.toLowerCase();
  const filteredDocs = query && documents
    ? documents.filter((doc) =>
        doc.original_filename.toLowerCase().includes(query) ||
        doc.error_message?.toLowerCase().includes(query)
      )
    : documents || [];

  const totalPages = Math.ceil(filteredDocs.length / PAGE_SIZE);
  const safePage = Math.min(page, Math.max(totalPages - 1, 0));
  const paginatedDocs = filteredDocs.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
  const selectedDoc = documents?.find((d) => d.id === selectedDocId) || null;

  const handleRetryAll = () => {
    if (!documents?.length) return;
    bulkRetry.mutate(documents.map((d) => d.id));
  };

  const handleDeleteAll = async () => {
    if (!documents?.length) return;
    setBulkDeleting(true);
    try {
      // Delete in sequence to avoid conflicts
      for (const doc of documents) {
        const { error } = await supabase
          .from("scanned_document")
          .delete()
          .eq("id", doc.id);
        if (error) throw error;
      }
      queryClient.invalidateQueries({ queryKey: ["scanned-documents"] });
      queryClient.invalidateQueries({ queryKey: ["document-stats"] });
      toast({
        title: "All failed documents deleted",
        description: `${documents.length} document${documents.length !== 1 ? 's' : ''} removed from the queue.`,
      });
      setSelectedDocId(null);
    } catch (error: any) {
      toast({
        title: "Error deleting documents",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setBulkDeleting(false);
      setShowBulkDeleteDialog(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-7xl w-[95vw] h-[90vh] flex flex-col overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Failed Documents
              <Badge variant="outline" className="text-sm font-normal">
                {documents?.length || 0} documents
              </Badge>
              <div className="ml-auto flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetryAll}
                  disabled={bulkRetry.isPending || !documents?.length}
                >
                  {bulkRetry.isPending ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      Retrying...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                      Retry All
                    </>
                  )}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowBulkDeleteDialog(true)}
                  disabled={bulkDeleting || !documents?.length}
                >
                  {bulkDeleting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                      Delete All
                    </>
                  )}
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-1 min-h-0">
            {/* Left pane - List */}
            <div className="w-[45%] flex flex-col border-r min-h-0">
              {/* Search */}
              <div className="relative px-4 py-3 shrink-0">
                <Search className="absolute left-7 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by filename or error..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                  className="pl-9 focus-visible:ring-1 focus-visible:ring-border focus-visible:ring-offset-0"
                />
              </div>

              {/* List */}
              <div className="flex-1 overflow-y-auto min-h-0">
                {isLoading ? (
                  <div className="space-y-3 p-4">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-14 w-full" />
                    ))}
                  </div>
                ) : !filteredDocs.length ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                    <CheckCircle2 className="h-10 w-10 text-green-500/50 mb-3" />
                    <p className="text-sm font-medium">
                      {search ? "No matching documents" : "No failed documents"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {search ? "Try a different search term" : "All documents are processing successfully"}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {paginatedDocs.map((doc) => {
                      const isSelected = selectedDocId === doc.id;
                      return (
                        <div
                          key={doc.id}
                          className={`px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors border-l-2 ${isSelected ? "border-l-red-500 bg-muted" : "border-l-transparent"}`}
                          onClick={() => setSelectedDocId(doc.id)}
                        >
                          <div className="flex items-center gap-2">
                            <FileStack className="h-4 w-4 text-muted-foreground shrink-0" />
                            <span className="text-sm font-medium truncate">{doc.original_filename}</span>
                            {doc.retry_count > 0 && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">
                                {doc.retry_count} retries
                              </Badge>
                            )}
                          </div>
                          {doc.error_message && (
                            <p className="text-xs text-red-500 truncate mt-1 ml-6">
                              {doc.error_message}
                            </p>
                          )}
                          <div className="flex items-center gap-3 mt-1 ml-6">
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(doc.detected_at), { addSuffix: true })}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-2 border-t shrink-0">
                  <p className="text-xs text-muted-foreground">
                    {safePage * PAGE_SIZE + 1}-{Math.min((safePage + 1) * PAGE_SIZE, filteredDocs.length)} of {filteredDocs.length}
                  </p>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 w-7 p-0"
                      disabled={safePage === 0}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <span className="text-xs px-1.5">
                      {safePage + 1} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 w-7 p-0"
                      disabled={safePage >= totalPages - 1}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Right pane - Detail */}
            <div className="w-[55%] min-h-0">
              {selectedDoc ? (
                <DetailPanel document={selectedDoc} />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                  <AlertTriangle className="h-12 w-12 mb-3 opacity-30" />
                  <p className="text-sm">Select a document to see details</p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete All Failed Documents</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete all {documents?.length || 0} failed documents from the processing queue?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAll}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
